import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { handlePrompt } from "../api/quoteAPI";

function QuoteCard() {
  const [quotes, setQuotes] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  // Fetch quotes from API (Appwrite function)
  useEffect(() => {
    const fetchQuotes = async () => {
      try {
        const { reply } = await handlePrompt();
        let cleanedQuotes = [];

        // Handle JSON array case directly
        if (Array.isArray(reply)) {
          cleanedQuotes = reply;
        } else if (typeof reply === "string") {
          let text = reply.trim();

          // Remove surrounding code fences, square brackets, and quotes
          text = text.replace(/```json|```/gi, "").trim();
          text = text.replace(/^\[|\]$/g, ""); // removes starting and ending [ ]

          // Split by commas between quotes
          const items = text.split(/["']?\s*,\s*["']?/);

          cleanedQuotes = items
            .map((q) =>
              q
                .replace(/^["'\s]+|["'\s]+$/g, "") // remove stray quotes & spaces
                .replace(/^\[|\]$/g, "") // remove leftover [ or ] from any element
                .trim()
            )
            .filter(Boolean);
        }

        // Final cleanup: remove any trailing brackets or quotes just in case
        cleanedQuotes = cleanedQuotes.map((q) =>
          q.replace(/^\[|\]$/g, "").replace(/^["'\s]+|["'\s]+$/g, "")
        );

        if (cleanedQuotes.length > 0) {
          setQuotes(cleanedQuotes);
        }
      } catch (err) {
        console.error("Error fetching quotes:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchQuotes();
  }, []);

  // Auto slide every 5 seconds
  useEffect(() => {
    if (!quotes.length) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % quotes.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [quotes]);

  if (loading || quotes.length === 0) return null;

  const currentQuote = quotes[currentIndex] ?? "";

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % quotes.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + quotes.length) % quotes.length);
  };

  return (
    <div className="relative w-full max-w-5xl mx-auto overflow-hidden rounded-2xl shadow-2xl bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 text-white p-8 mt-12 min-h-[220px] flex items-center justify-center">
      {/* Animated Quote */}
      <AnimatePresence mode="wait">
        <motion.p
          key={currentQuote}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 1.0, ease: "easeInOut" }}
          className="text-center text-xl sm:text-2xl font-medium leading-relaxed max-w-2xl"
        >
          {currentQuote}
        </motion.p>
      </AnimatePresence>

      {/* Navigation Buttons */}
      <button
        onClick={prevSlide}
        className="absolute top-1/2 left-4 -translate-y-1/2 bg-white/20 hover:bg-white/40 p-2 rounded-full transition"
        aria-label="Previous quote"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 6 10"
          className="w-4 h-4 text-white"
        >
          <path
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M5 1 1 5l4 4"
          />
        </svg>
      </button>

      <button
        onClick={nextSlide}
        className="absolute top-1/2 right-4 -translate-y-1/2 bg-white/20 hover:bg-white/40 p-2 rounded-full transition"
        aria-label="Next quote"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 6 10"
          className="w-4 h-4 text-white"
        >
          <path
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="m1 9 4-4-4-4"
          />
        </svg>
      </button>

      {/* Dots Indicator */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
        {quotes.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`w-3 h-3 rounded-full transition-all ${
              index === currentIndex ? "bg-white" : "bg-white/40"
            }`}
          ></button>
        ))}
      </div>
    </div>
  );
}

export default QuoteCard;
